/**
 * DataTable organism - comprehensive data table with all common features
 * Replaces scattered table implementations with a unified, feature-rich component
 */

import {
  Box,
  ButtonGroup,
  HStack,
  Select,
  Skeleton,
  Table,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  useColorModeValue,
  VStack,
} from '@chakra-ui/react';
import React, { useMemo } from 'react';
import { FiChevronLeft, FiChevronRight } from 'react-icons/fi';
import { Button, IconButton } from '../../atoms/buttons';
import { Card } from '../../atoms/layouts/Card';
import { Text } from '../../atoms/typography/Text';
import { SearchBar, SearchBarProps } from '../../molecules/forms/SearchBar';
import { TableProps } from '../../types';

export interface DataTableProps<T = any> extends TableProps<T> {
  /** Use tighter container and section spacing. */
  compact?: boolean;
  /** Table title */
  title?: string;
  /** Search functionality props */
  search?: Omit<SearchBarProps, 'className' | 'testId'>;
  /** Whether to show search bar */
  showSearch?: boolean;
  /** Whether table is searchable (legacy prop) */
  searchable?: boolean;
  /** Search placeholder text (legacy prop) */
  searchPlaceholder?: string;
  /** Additional actions in header */
  headerActions?: React.ReactNode;
  /** Custom empty state */
  emptyState?: React.ReactNode;
  /** Table size */
  size?: 'sm' | 'md' | 'lg';
  /** Whether table is striped */
  striped?: boolean;
  /** Message to display when data is empty */
  emptyMessage?: string;
}

export const DataTable = <T extends Record<string, any>>({
  title,
  compact = false,
  search,
  showSearch = false,
  searchable,
  searchPlaceholder,
  headerActions,
  emptyState,
  data,
  columns,
  loading = false,
  error,
  onRowClick,
  clickableRows = false,
  actions = [],
  pagination,
  size = 'md',
  striped = true,
  className,
  testId,
}: DataTableProps<T>) => {
  const headerBg = useColorModeValue('gray.50', 'gray.700');
  const borderColor = useColorModeValue('gray.200', 'gray.600');

  // Calculate visible data based on pagination
  const visibleData = useMemo(() => {
    if (!pagination) return data;

    const startIndex =
      (pagination.currentPage - 1) * (pagination.pageSize || 10);
    const endIndex = startIndex + (pagination.pageSize || 10);
    return data.slice(startIndex, endIndex);
  }, [data, pagination]);

  const hasActions = actions.length > 0;
  const isEmpty = !loading && visibleData.length === 0;

  if (error) {
    return (
      <Card
        className={className}
        data-testid={testId}
        padding={compact ? 3 : 6}
      >
        <VStack spacing={4} py={8}>
          <Text variant="error">Error loading data</Text>
          <Text variant="caption">{error.toString()}</Text>
        </VStack>
      </Card>
    );
  }

  return (
    <Card className={className} data-testid={testId} padding={compact ? 3 : 6}>
      <VStack spacing={compact ? 3 : 4} align="stretch">
        {/* Header */}
        {(title || showSearch || searchable || headerActions) && (
          <VStack spacing={compact ? 3 : 4} align="stretch">
            {(title || headerActions) && (
              <HStack
                justify="space-between"
                align="center"
                flexWrap={compact ? 'wrap' : undefined}
                gap={compact ? 2 : undefined}
              >
                {title && (
                  <Text fontSize="lg" fontWeight="semibold">
                    {title}
                  </Text>
                )}
                {headerActions && <Box>{headerActions}</Box>}
              </HStack>
            )}

            {(showSearch && search) || searchable ? (
              <SearchBar
                {...search}
                placeholder={searchPlaceholder || search?.placeholder}
              />
            ) : null}
          </VStack>
        )}

        {/* Table */}
        {loading ? (
          <VStack spacing={3}>
            {Array.from({ length: 5 }).map((_, index) => (
              <Skeleton key={index} height="40px" width="100%" />
            ))}
          </VStack>
        ) : isEmpty ? (
          <VStack spacing={compact ? 2 : 4} py={compact ? 4 : 12}>
            {emptyState || (
              <>
                <Text variant="subtitle">No data available</Text>
                <Text variant="caption">No records match your criteria</Text>
              </>
            )}
          </VStack>
        ) : (
          <Box overflowX="auto">
            <Table variant={striped ? 'striped' : 'simple'} size={size}>
              <Thead bg={headerBg}>
                <Tr>
                  {columns.map((column, index) => (
                    <Th
                      key={String(column.key)}
                      width={column.width}
                      borderColor={borderColor}
                    >
                      {column.header}
                    </Th>
                  ))}
                  {hasActions && <Th width="120px">Actions</Th>}
                </Tr>
              </Thead>
              <Tbody>
                {visibleData.map((item, rowIndex) => (
                  <Tr
                    key={rowIndex}
                    cursor={clickableRows ? 'pointer' : 'default'}
                    _hover={clickableRows ? { bg: headerBg } : {}}
                    onClick={() =>
                      clickableRows && onRowClick?.(item, rowIndex)
                    }
                  >
                    {columns.map((column) => (
                      <Td key={String(column.key)} borderColor={borderColor}>
                        {column.render
                          ? column.render(item, item[column.key], rowIndex)
                          : item[column.key]}
                      </Td>
                    ))}
                    {hasActions && (
                      <Td borderColor={borderColor}>
                        <HStack spacing={2}>
                          {actions.map((action, actionIndex) =>
                            action.icon ? (
                              <IconButton
                                key={actionIndex}
                                icon={action.icon}
                                label={action.label}
                                size="sm"
                                variant={action.variant || 'secondary'}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  action.onClick(item);
                                }}
                              />
                            ) : (
                              <Button
                                key={actionIndex}
                                size="sm"
                                variant={action.variant || 'secondary'}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  action.onClick(item);
                                }}
                              >
                                {action.label}
                              </Button>
                            ),
                          )}
                        </HStack>
                      </Td>
                    )}
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </Box>
        )}

        {/* Pagination */}
        {pagination && pagination.totalPages > 1 && (
          <VStack spacing={compact ? 3 : 4} align="stretch">
            <HStack
              justify="space-between"
              align="center"
              flexWrap={compact ? 'wrap' : undefined}
              gap={compact ? 2 : undefined}
            >
              <VStack spacing={1} align="flex-start">
                <Text variant="caption">
                  Page {pagination.currentPage} of {pagination.totalPages}
                </Text>
                {pagination.totalItems && (
                  <Text variant="caption" color="gray.500">
                    Showing{' '}
                    {Math.min(
                      (pagination.currentPage - 1) *
                        (pagination.pageSize || 10) +
                        1,
                      pagination.totalItems,
                    )}
                    -
                    {Math.min(
                      pagination.currentPage * (pagination.pageSize || 10),
                      pagination.totalItems,
                    )}{' '}
                    of {pagination.totalItems} rows
                  </Text>
                )}
              </VStack>

              <HStack spacing={4}>
                {pagination.showPageSizeSelector &&
                  pagination.onPageSizeChange && (
                    <HStack spacing={2} align="center">
                      <Text variant="caption" fontSize="sm">
                        Rows per page:
                      </Text>
                      <Select
                        size="sm"
                        value={pagination.pageSize || 10}
                        onChange={(e) =>
                          pagination.onPageSizeChange!(Number(e.target.value))
                        }
                        width="70px"
                      >
                        <option value={5}>5</option>
                        <option value={10}>10</option>
                        <option value={25}>25</option>
                        <option value={50}>50</option>
                        <option value={100}>100</option>
                      </Select>
                    </HStack>
                  )}

                <ButtonGroup size="sm" isAttached>
                  <IconButton
                    icon={<FiChevronLeft />}
                    label="Previous page"
                    isDisabled={pagination.currentPage <= 1}
                    onClick={() =>
                      pagination.onPageChange(pagination.currentPage - 1)
                    }
                  />
                  <IconButton
                    icon={<FiChevronRight />}
                    label="Next page"
                    isDisabled={pagination.currentPage >= pagination.totalPages}
                    onClick={() =>
                      pagination.onPageChange(pagination.currentPage + 1)
                    }
                  />
                </ButtonGroup>
              </HStack>
            </HStack>
          </VStack>
        )}
      </VStack>
    </Card>
  );
};
